/**
 *  @namespace ScrollVideoCore
 *
 *  Shared engine for scroll-driven / progressive video sections
 *  (scroll-video, scroll-video-split). Pure helpers on `window`,
 *  no side effects on load.
 */
if (!window.ScrollVideoCore) {
	window.ScrollVideoCore = (() => {
		const prefersHevc = (video) => video.canPlayType('video/mp4; codecs="hvc1"') !== "";

		// Size tier by viewport width (small < 750, medium < 1100, large
		// otherwise). Missing tiers fall back to the next larger file; if the
		// preferred format has no URL at all, fall back to the other format so
		// a single-format (e.g. opaque mp4-only) setup still plays everywhere
		// the codec is supported.
		const tierChain = (d, format, tier) =>
			({
				small: [d[format + "Small"], d[format + "Medium"], d[format]],
				medium: [d[format + "Medium"], d[format]],
				large: [d[format]],
			})[tier];

		function pickSource(d, video, viewportWidth) {
			const primary = prefersHevc(video) ? "mov" : "webm";
			const secondary = primary === "mov" ? "webm" : "mov";
			const tier = viewportWidth < 750 ? "small" : viewportWidth < 1100 ? "medium" : "large";
			return (
				tierChain(d, primary, tier)
					.concat(tierChain(d, secondary, tier))
					.find((url) => url) || null
			);
		}

		function pickLowSource(d, video) {
			const primary = prefersHevc(video) ? "mov" : "webm";
			const secondary = primary === "mov" ? "webm" : "mov";
			return d[primary + "Low"] || d[secondary + "Low"] || null;
		}

		// Data-saver or low-memory devices stay on the low-res rendition when
		// one exists. Detection APIs are Chromium-only: absent APIs silently
		// count as "not constrained" (Safari never blocks the hi-res swap).
		function isConstrained() {
			return Boolean(
				(navigator.connection && navigator.connection.saveData) ||
				(navigator.deviceMemory && navigator.deviceMemory <= 4),
			);
		}

		// Download the whole file and hand back a blob: URL so every seek lands
		// in fully buffered data (streaming src gets throttled by the browser
		// and seeks into unbuffered ranges stall the decode pipeline).
		function blobLoad(url, options) {
			const init = options && options.lowPriority ? { priority: "low" } : undefined;
			return fetch(url, init)
				.then((response) => {
					if (!response.ok) {
						throw new Error("HTTP " + response.status);
					}
					return response.blob();
				})
				.then((blob) => URL.createObjectURL(blob));
		}

		// Low-res first (fast to fetch, playable/scrubbable in ~a second), then
		// hot-swap to the hi-res blob: remember position + play state, replace
		// src, restore. Falls back gracefully at every stage; hi-res fetch is
		// skipped entirely on constrained devices.
		function progressiveAttach(video, opts) {
			let destroyed = false;
			let lowObjectUrl = null;
			let hiObjectUrl = null;

			const attach = (src) => {
				if (destroyed) return;
				// Markup ships preload="none" so the element stays inert until the
				// lazy observer decides to load it. Once we hand it a blob the file
				// is already fully in memory, so keeping "none" costs no bandwidth —
				// it only stops the decoder from producing frame 0, leaving the
				// poster on screen until play() runs. Gated loops (manifest mode)
				// wait for their dock's show window to play, so that gap was
				// showing Shopify's auto-generated poster as a grey first frame.
				video.preload = "auto";
				video.src = src;
				video.load();
				if (opts.onReady) opts.onReady();
			};

			/* opts.streaming: play while downloading, skipping the whole-file download (2026-07-27).
			 *
			 * What the blobLoad comment above says — "a streaming src gets throttled, and seeking into an unbuffered range stalls the decode
			 * pipeline" — **only holds for scrub videos**: those seek to arbitrary positions and must have the whole file
			 * in hand. Loop videos are different: they only play forward, and the only seek is currentTime = 0 (starting playback when the gate
			 * opens, rewinding when scrolled back), a position that is always buffered.
			 *
			 * The gain is large: the three right-column videos are 2.3~4.4MB on the Shopify CDN, and a full download takes 13.7s /
			 * 33.7s respectively, during which the element shows nothing; with play-while-downloading the first frame appears in ~1s.
			 *
			 * ⚠️ The left-column long strip must **never** enable this (guarded by tests/unit/svs-loop-streaming.test.mjs). */
			if (opts.streaming) {
				attach(opts.hiUrl || opts.lowUrl);
				return {
					// Streaming has no second tier to swap to, so both methods are no-ops, but they must exist:
					// the caller pushes the handle into handles and calls destroy on all of them at unmount.
					startHi() {},
					destroy() {
						destroyed = true;
					},
				};
			}

			const swap = (src) => {
				if (destroyed) return;
				const position = video.currentTime;
				const wasPlaying = !video.paused && !video.ended;
				video.src = src;
				video.load();
				video.addEventListener(
					"loadedmetadata",
					() => {
						if (destroyed) return;
						video.currentTime = position;
						if (wasPlaying) video.play().catch(() => {});
						if (lowObjectUrl) {
							URL.revokeObjectURL(lowObjectUrl);
							lowObjectUrl = null;
						}
						if (opts.onSwap) opts.onSwap();
					},
					{ once: true },
				);
			};

			// Once HD is ready we don't necessarily swap immediately: swap resets src and waits for loadedmetadata, and for tens to hundreds
			// of milliseconds the picture is uncontrolled, which is visible if it lands during product motion. Poll opts.canSwapNow(ms waited),
			// letting the caller decide when it is safe (scroll-video-split only allows it within the dock band or when the section is offscreen).
			// Without that callback the old behavior of swapping immediately is kept — the right-column loop videos rely on this.
			const swapWhenSafe = (src) => {
				if (destroyed) return;
				if (typeof opts.canSwapNow !== "function") {
					swap(src);
					return;
				}
				const startedAt = Date.now();
				const attempt = () => {
					if (destroyed) return;
					if (opts.canSwapNow(Date.now() - startedAt)) swap(src);
					else requestAnimationFrame(attempt);
				};
				attempt();
			};

			// lowPriority only affects the fetch priority (which must be decided when the request is issued). Whether to swap or attach
			// must be decided **at the moment HD becomes ready**, based on the current state of lowObjectUrl — the criterion is the state "is a
			// low-res version playing right now", not the intent "did we want to swap when the request was issued".
			//
			// 2026-07-27 real-device pitfall: the caller's loadObserver rootMargin is 800% (covering the whole page),
			// so startHi() fires almost as soon as the page loads, when the low-res blob hasn't finished downloading → judged as "no low-res to swap"
			// → attach path. When the 7MB finished it overwrote directly, so onSwap never fired (videoTier never reached hi,
			// the layer stood in permanently), the canSwapNow timing guard was bypassed, and the seeker's frame grid was never realigned.
			const loadHi = (lowPriority) =>
				blobLoad(opts.hiUrl, { lowPriority: lowPriority })
					.then((objectUrl) => {
						if (destroyed) {
							URL.revokeObjectURL(objectUrl);
							return;
						}
						hiObjectUrl = objectUrl;
						const viaSwap = Boolean(lowObjectUrl);
						(viaSwap ? swapWhenSafe : attach)(objectUrl);
					})
					.catch(() => {
						// Fall back to the streaming src only when no low-res is playing; if low-res is playing, stay on it.
						if (!lowObjectUrl) attach(opts.hiUrl);
					});

			// opts.holdHi: the caller decides for itself when to start downloading HD (e.g. waiting for IntersectionObserver to say
			// "almost scrolled there"). A 7MB-class asset should not start downloading during first-screen idle time — that is pure waste
			// for visitors who never scroll to this section. When unset, the old behavior is kept (start HD right after getting low-res).
			let hiStarted = false;
			const startHi = (lowPriority) => {
				if (hiStarted || destroyed || !opts.hiUrl) return;
				hiStarted = true;
				loadHi(lowPriority);
			};

			if (opts.lowUrl) {
				blobLoad(opts.lowUrl)
					.then((objectUrl) => {
						if (destroyed) {
							URL.revokeObjectURL(objectUrl);
							return;
						}
						lowObjectUrl = objectUrl;
						attach(objectUrl);
						if (opts.hiUrl && !isConstrained() && !opts.holdHi) startHi(true);
					})
					.catch(() => {
						if (opts.hiUrl) startHi(false);
						else attach(opts.lowUrl);
					});
			} else if (opts.hiUrl && !opts.holdHi) {
				startHi(false);
			}

			return {
				// If there is a low-res tier, give HD low priority so it doesn't compete for bandwidth.
				// The swap/attach choice is not made here — see the long comment in loadHi.
				// Repeated calls are harmless (hiStarted blocks them).
				startHi() {
					startHi(Boolean(opts.lowUrl));
				},
				destroy() {
					destroyed = true;
					if (lowObjectUrl) URL.revokeObjectURL(lowObjectUrl);
					if (hiObjectUrl) URL.revokeObjectURL(hiObjectUrl);
				},
			};
		}

		// Time-based smoothing (~140ms constant, identical feel on 60/120Hz)
		// plus frame-grid quantization (options.frameStep, default 1/30s):
		// sub-frame seeks show no new pixels but still cost a full decode.
		// Pass a smaller frameStep for high-fps assets — perceived scrub
		// smoothness is bounded by the asset's frame grid, and measured seek
		// latency is ~3-4ms, so decode is not the limiter. A seek stuck
		// > 100ms can be overridden so one slow decode never stalls the
		// scrub (this flag is also the decoder backpressure — do not add
		// velocity-based frame skipping on top, it only coarsens motion).
		function createSmoothSeeker(video, options) {
			const frameStep = (options && options.frameStep) || 1 / 30;
			// `let`, not `const`: a section whose scroll budget changes at runtime
			// (pinned vs. free playback) needs a different time constant per mode —
			// see setSmoothMs below.
			let smoothMs = (options && options.smoothMs) || 140;
			// Snap once the remaining glide is within one frame: the
			// exponential tail otherwise dribbles out as sporadic single-frame
			// hops ("tick…tick…stop") right where the artwork comes to rest —
			// perceived as a subtle stutter at every dock.
			const snapWindow = Math.max(0.002, frameStep);
			// Seek pipelining: release the next seek on whichever signal
			// arrives first — the previous frame reaching the screen
			// (requestVideoFrameCallback) or the decoder reporting the seek
			// finished (`seeking` cleared). 100ms override on top so a stuck
			// seek can never wedge the loop.
			//
			// Both signals are needed because each browser is slow on a
			// different one (measured on the real asset, seeks/sec, higher is
			// better — 60 is the ceiling at 60Hz):
			//
			//                  rVFC only   seeking only   either (this code)
			//   Safari 26.3         21.3           52.3                 52.7
			//   Chrome 150          55.7           43.7                 57.7
			//
			// Waiting on `seeking` alone serialises the round-trip (decode +
			// upload + present) and caps the rate at 1/latency — that is the
			// Chrome column, and why rVFC was introduced in the first place
			// ("no dropped rAF frames, yet the artwork visibly steps").
			// But Safari fires rVFC late and erratically for a PAUSED video
			// being seeked: a quarter of the seeks only got through on the
			// 100ms override, so the scrub ran at ~21fps while the page
			// scrolled at 60 — reported as "Safari stutters very noticeably, Chrome is fine"
			// (2026-07-21). Its `seeking` flag, meanwhile, clears in ~7ms.
			// Neither signal is trustworthy alone; the first one to arrive is.
			const hasVfc = typeof video.requestVideoFrameCallback === "function";
			let awaitingFrame = false;
			let smoothedTime = 0;
			let lastWrittenTime = -1;
			let lastSeekStart = 0;
			let justActivated = true;

			return {
				reset() {
					justActivated = true;
				},
				invalidate() {
					lastWrittenTime = -1;
				},
				// Swap the smoothing time constant mid-flight. A short scroll
				// budget needs a short constant: the glide is a fixed *time*,
				// so on a 400px run at trackpad speed the tail lands after the
				// artwork has already left the screen. Cheap enough to call
				// every frame.
				setSmoothMs(ms) {
					if (ms > 0) smoothMs = ms;
				},
				/*
				 * `snap` skips the glide and writes the target immediately.
				 * Pass it at the very end of a run whose remaining scroll is
				 * shorter than the glide: the exponential tail would otherwise
				 * deliver the last frames after the element is out of view —
				 * i.e. the viewer never sees the end of the clip.
				 */
				update(targetSeconds, dtMs, snap) {
					if (justActivated || snap) {
						smoothedTime = targetSeconds;
						justActivated = false;
					} else {
						const alpha = 1 - Math.exp(-dtMs / smoothMs);
						smoothedTime += (targetSeconds - smoothedTime) * alpha;
						if (Math.abs(targetSeconds - smoothedTime) < snapWindow) {
							smoothedTime = targetSeconds;
						}
					}
					const quantized = Math.round(smoothedTime / frameStep) * frameStep;
					const now = performance.now();
					const canSeek = !video.seeking || (hasVfc && !awaitingFrame) || now - lastSeekStart > 100;
					if (canSeek && Math.abs(quantized - lastWrittenTime) >= frameStep * 0.9) {
						video.currentTime = quantized;
						lastWrittenTime = quantized;
						lastSeekStart = now;
						if (hasVfc) {
							awaitingFrame = true;
							video.requestVideoFrameCallback(() => {
								awaitingFrame = false;
							});
						}
					}
				},
			};
		}

		// iOS: seeking is only reliable after playback has been unlocked by a
		// user gesture. play().then(pause) forces the decoder to warm up.
		function installTouchUnlock(video, reducedMotion) {
			const unlock = () => {
				if (reducedMotion || !video) return;
				video
					.play()
					.then(() => video.pause())
					.catch(() => {});
			};
			window.addEventListener("touchstart", unlock, { once: true, passive: true });
			return () => window.removeEventListener("touchstart", unlock);
		}

		// Where the left strip's FRAME actually sits, given its column box.
		// The strip is laid out at column width and its natural height, top
		// aligned, with the column clipping it — so the frame is as wide as the
		// column, starts at its top edge, and runs taller than it.
		//
		// Shared because two files need this and they must never disagree:
		// scroll-video-split derives dock frames and annotation anchors from
		// it, section-handoff derives the take-off rect for the flight into the
		// next section. They used to carry separate copies kept in sync by a
		// comment; when the strip switched from object-fit to a clipping window
		// only one copy was updated, and the handoff took off 827px away from
		// the artwork — the flight simply vanished (2026-07-20).
		function stripFrame(video, box) {
			if (!video || !video.videoWidth || !video.videoHeight || box.width <= 0) {
				return { left: box.left, top: box.top, width: box.width, height: box.height };
			}
			return {
				left: box.left,
				top: box.top,
				width: box.width,
				height: video.videoHeight * (box.width / video.videoWidth),
			};
		}

		return {
			stripFrame,
			pickSource,
			pickLowSource,
			isConstrained,
			blobLoad,
			progressiveAttach,
			createSmoothSeeker,
			installTouchUnlock,
		};
	})();
}
